/** Bloc de contenu : le conteneur de base de tous les écrans du back-office. */

import type { ReactNode } from 'react'

import { cn } from '../lib/cn'

export interface CardProps {
  readonly className?: string | undefined
  readonly children: ReactNode
}

export function Card({ className, children }: CardProps) {
  return (
    <section
      className={cn(
        'rounded-carte border border-slate-200 bg-white shadow-sm',
        className,
      )}
    >
      {children}
    </section>
  )
}

export interface CardEnteteProps {
  readonly titre: ReactNode
  readonly description?: ReactNode | undefined
  /** Boutons alignés à droite du titre. */
  readonly actions?: ReactNode | undefined
  readonly className?: string | undefined
}

export function CardEntete({ titre, description, actions, className }: CardEnteteProps) {
  return (
    <header
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-slate-900">{titre}</h2>
        {description !== undefined && (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        )}
      </div>
      {/* `max-w-full` : sur un téléphone, les actions ne doivent jamais pousser
          la carte au-delà de la largeur de l’écran — elles passent à la ligne
          plutôt que de déborder (le `shrink-0` seul créait 500 px de scroll
          horizontal sur /admin/tableaux). */}
      {actions !== undefined && (
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </header>
  )
}

export function CardCorps({ className, children }: CardProps) {
  return <div className={cn('px-5 py-4', className)}>{children}</div>
}

export function CardPied({ className, children }: CardProps) {
  return (
    <footer
      className={cn(
        'flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3',
        className,
      )}
    >
      {children}
    </footer>
  )
}

/**
 * Chiffre clé d’un tableau de bord (DASH-01…03).
 * `valeur` accepte un `<Money>` aussi bien qu’un nombre formaté.
 */
export interface StatProps {
  readonly libelle: ReactNode
  readonly valeur: ReactNode
  readonly precision?: ReactNode | undefined
  readonly className?: string | undefined
}

export function Stat({ libelle, valeur, precision, className }: StatProps) {
  return (
    <div className={cn('rounded-carte border border-slate-200 bg-white px-4 py-3', className)}>
      <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">{libelle}</p>
      <p className="chiffres-alignes mt-1 text-2xl font-semibold text-slate-900">{valeur}</p>
      {precision !== undefined && <p className="mt-0.5 text-xs text-slate-500">{precision}</p>}
    </div>
  )
}
